// Page "Réglages" (thème, consigne permanente, déconnexion).
import { bindActivate, errorMessage, describeActionError } from "../ui/domHelpers";
import { createDropdown, DropdownHandle } from "../ui/dropdown";
import { applyTheme, getStoredTheme } from "../ui/theme";
import { logout } from "../graph";
import { mailData } from "../mailData";
import { getSettings, saveSettings } from "../praetoria";

let themeDropdown: DropdownHandle;

// Appelé une fois au bootstrap (showAppBody), comme les autres dropdowns de l'app.
export function initThemeDropdown(): void {
  themeDropdown = createDropdown("settings-theme-dropdown", applyTheme);
}

function hideSettingsPage(): void {
  document.getElementById("settings-page").style.display = "none";
  document.getElementById("app-body").style.display = "flex";
}

// onAfterLogout est injecté (resetReplyPanel vit dans le moteur de génération, outlook.ts) pour ne
// pas faire dépendre ce module de l'état de génération/cache de réponse.
export async function showSettingsPage(onAfterLogout: () => void): Promise<void> {
  document.getElementById("app-body").style.display = "none";
  document.getElementById("settings-page").style.display = "flex";
  bindActivate("settings-cancel", hideSettingsPage);
  bindActivate("settings-save", () => void handleSaveSettings());
  bindActivate("settings-logout", () => void handleLogout(onAfterLogout));
  themeDropdown.setValue(getStoredTheme());

  const consigneElement = document.getElementById("settings-consigne") as HTMLTextAreaElement;
  const statusElement = document.getElementById("settings-status");
  statusElement.textContent = "";
  statusElement.className = "reply-status";

  if (!mailData.graphToken) {
    return;
  }
  try {
    const settings = await getSettings(mailData.graphToken);
    consigneElement.value = settings.consignes_constantes || "";
  } catch (error) {
    statusElement.textContent = describeActionError("Impossible de charger les paramètres actuels", error);
    statusElement.className = "reply-status reply-status--error";
  }
}

async function handleSaveSettings(): Promise<void> {
  const consigneElement = document.getElementById("settings-consigne") as HTMLTextAreaElement;
  const statusElement = document.getElementById("settings-status");
  const saveButton = document.getElementById("settings-save");

  if (!mailData.graphToken) {
    statusElement.textContent = "Session Microsoft expirée, rechargez le module.";
    statusElement.className = "reply-status reply-status--error";
    return;
  }

  saveButton.classList.add("is-disabled");
  saveButton.setAttribute("aria-disabled", "true");
  statusElement.textContent = "";
  statusElement.className = "reply-status";

  try {
    await saveSettings(mailData.graphToken, { consignes_constantes: consigneElement.value });
    statusElement.textContent = "Paramètres enregistrés.";
  } catch (error) {
    statusElement.textContent = describeActionError("Impossible d'enregistrer les paramètres", error);
    statusElement.className = "reply-status reply-status--error";
  } finally {
    saveButton.classList.remove("is-disabled");
    saveButton.removeAttribute("aria-disabled");
  }
}

// logout() vide tout localStorage (cache MSAL + cache des réponses/consignes, voir graph.ts). Sur
// certains clients Outlook (déjà vu avec le panneau épinglé/manifest XML legacy, voir le handler
// ItemChanged dans outlook.ts), fermer puis rouvrir le taskpane ne recharge pas forcément la page --
// Outlook peut réafficher le même contexte JS déjà chargé. Sans ça, la réponse générée resterait
// visible à l'écran (dans le DOM/l'état JS en mémoire) même si son cache localStorage a bien été
// vidé. On réinitialise donc explicitement le panneau de réponse (onAfterLogout) avant de fermer,
// pour que le résultat soit correct qu'Outlook recharge vraiment la page ou non.
// On ferme ensuite le taskpane (closeContainer, nécessite Mailbox 1.5) plutôt que de le recharger :
// rouvrir l'add-in redemanderait de toute façon un token, et resolveIdentity() le fait
// silencieusement si la session Microsoft/Outlook est toujours active -- fermer évite de laisser
// affiché un taskpane "vide" qui semble ne rien avoir fait. Fallback sur un simple reload si
// closeContainer n'est pas supporté par ce client Outlook. Ne force pas de changement de compte :
// ça reste piloté par la session M365 active, voir le commentaire dans graph.ts.
async function handleLogout(onAfterLogout: () => void): Promise<void> {
  const statusElement = document.getElementById("settings-logout-status");
  const logoutButton = document.getElementById("settings-logout");
  logoutButton.classList.add("is-disabled");
  logoutButton.setAttribute("aria-disabled", "true");
  statusElement.textContent = "Déconnexion en cours...";
  statusElement.className = "reply-status";
  try {
    await logout();
    onAfterLogout();
    if (Office.context.requirements.isSetSupported("Mailbox", "1.5")) {
      Office.context.ui.closeContainer();
    } else {
      location.reload();
    }
  } catch (error) {
    statusElement.textContent = `Impossible de se déconnecter (${errorMessage(error)}).`;
    statusElement.className = "reply-status reply-status--error";
    logoutButton.classList.remove("is-disabled");
    logoutButton.removeAttribute("aria-disabled");
  }
}
